import React from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import heritagevideo from "../assets/images/heritagevideo.mp4";

const HeritagePage = () => {
  const navigate = useNavigate();

  const values = [
    {
      number: "01",
      title: "Thoughtfully Chosen",
      text: "Every saree is selected for its beauty, texture, colour and timeless appeal.",
    },
    {
      number: "02",
      title: "Beautiful Weaves",
      text: "From rich silks to comfortable cottons, we celebrate the character of every weave.",
    },
    {
      number: "03",
      title: "Made for Moments",
      text: "Sarees curated for celebrations, special occasions and everyday elegance.",
    },
  ];

  return (
    <main className="bg-[#FDFBF7] text-[#4A0E17]">

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden px-5 py-20 sm:py-28">

        {/* Soft glow */}
        <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-[#C19A5B]/10 blur-3xl" />

        <div className="relative mx-auto max-w-3xl text-center">

          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#E8DFC8] bg-[#F7F1E8] px-4 py-2 text-[9px] font-semibold uppercase tracking-[0.25em] text-[#6D1835]">
            <Sparkles className="h-3.5 w-3.5 text-[#C19A5B]" />
            Our Story
          </div>

          <h1 className="font-serif text-4xl font-medium leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Elegance,
            <span className="block text-[#C19A5B]">
              Woven with Intention.
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-xl text-sm leading-7 text-[#806F69] sm:text-base">
            VEDHYAS is a thoughtfully curated saree destination,
            bringing together beautiful fabrics, graceful colours
            and timeless styles for the modern woman.
          </p>

        </div>
      </section>


      {/* ================= STORY ================= */}
      <section className="px-5 pb-20 sm:pb-24">

        <div className="mx-auto grid max-w-6xl items-center gap-10 md:grid-cols-2">

          <div className="group relative overflow-hidden rounded-[28px]">

  <video
    className="h-[430px] w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105 sm:h-[520px]"
    autoPlay
    muted
    loop
    playsInline
    preload="metadata"
  >
    <source src={heritagevideo} type="video/mp4" />
  </video>

  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2B080F]/40 to-transparent" />

  {/* Luxury shine */}
  <div className="pointer-events-none absolute -left-1/2 top-0 h-full w-1/3 rotate-[18deg] bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 blur-md transition-all duration-700 group-hover:left-[120%] group-hover:opacity-100" />

</div>


          {/* Content */}
          <div className="max-w-lg">

            <span className="text-[9px] font-semibold uppercase tracking-[0.25em] text-[#C19A5B]">
              The VEDHYAS Philosophy
            </span>

            <h2 className="mt-3 font-serif text-3xl font-medium leading-tight sm:text-4xl">
              Simple. Elegant.
              <span className="block">
                Timeless.
              </span>
            </h2>

            <p className="mt-5 text-sm leading-7 text-[#806F69]">
              We believe a beautiful saree does not need to follow
              every trend. It should feel special today and remain
              beautiful for years to come.
            </p>

            <p className="mt-4 text-sm leading-7 text-[#806F69]">
              Our collections bring together different textures,
              colours and fabrics so you can find something that
              naturally feels like you.
            </p>

            <button
              onClick={() => navigate("/catalog")}
              className="mt-7 inline-flex items-center gap-3 rounded-full bg-[#4A0E17] px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#641329] hover:shadow-lg"
            >
              Explore Sarees
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>

          </div>

        </div>
      </section>


      {/* ================= VALUES ================= */}
      <section className="border-y border-[#E8DFC8] bg-[#F8F3EC] px-5 py-16 sm:py-20">

        <div className="mx-auto max-w-6xl">

          <div className="mb-10 text-center">

            <span className="text-[9px] font-semibold uppercase tracking-[0.25em] text-[#C19A5B]">
              What We Value
            </span>

            <h2 className="mt-2 font-serif text-3xl font-medium">
              The VEDHYAS Way
            </h2>

          </div>


          <div className="grid gap-px overflow-hidden rounded-[24px] border border-[#E8DFC8] bg-[#E8DFC8] md:grid-cols-3">

            {values.map((item) => (

              <div
                key={item.number}
                className="group bg-[#FDFBF7] p-7 transition-all duration-500 hover:bg-white sm:p-9"
              >

                <span className="font-serif text-sm text-[#C19A5B]">
                  {item.number}
                </span>

                <h3 className="mt-5 font-serif text-xl font-medium text-[#4A0E17]">
                  {item.title}
                </h3>

                <p className="mt-3 text-xs leading-6 text-[#806F69]">
                  {item.text}
                </p>

                <div className="mt-6 h-px w-8 bg-[#C19A5B] transition-all duration-500 group-hover:w-16" />

              </div>

            ))}

          </div>

        </div>
      </section>


      {/* ================= FINAL MESSAGE ================= */}
      <section className="px-5 py-20 text-center sm:py-24">

        <div className="mx-auto max-w-2xl">

          <Sparkles className="mx-auto h-5 w-5 text-[#C19A5B]" />

          <h2 className="mt-4 font-serif text-3xl font-medium sm:text-4xl">
            Find something
            <span className="text-[#C19A5B]">
              {" "}beautiful.
            </span>
          </h2>

          <p className="mx-auto mt-4 max-w-md text-xs leading-6 text-[#806F69]">
            Explore the VEDHYAS collection and discover sarees
            chosen for their beauty, comfort and timeless appeal.
          </p>

          <button
            onClick={() => navigate("/catalog")}
            className="mt-7 inline-flex items-center gap-2 rounded-full border border-[#4A0E17] px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#4A0E17] transition-all duration-300 hover:bg-[#4A0E17] hover:text-white"
          >
            Explore Collection
            <ArrowRight className="h-4 w-4" />
          </button>

        </div>

      </section>

    </main>
  );
};

export default HeritagePage;